<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { block, field, patch } = useInlineBlockEditor(props.id);

const individualReportPath = field<string>("individualReportPath", "");
const sortColumn = field<string>("sortColumn", "");
const colorAccessor = field<string>("colorAccessor", "(d, i) => null");
const iconAccessor = field<string>("iconAccessor", "(d, i) => null");

const selectedConstructIds = computed({
  get: () => ((block.value?.selectedConstructIds ?? []) as string[]).join(", "),
  set: (value: string) => {
    patch({ selectedConstructIds: value.split(",").map((item) => item.trim()).filter(Boolean) });
  },
});

const sortAscending = computed({
  get: () => (block.value?.sortOrder ?? "desc") === "asc",
  set: (value: boolean) => patch({ sortOrder: value ? "asc" : "desc" }),
});
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:rocket" accent="reporting">
    <EditorField :label="t('individualReportPathLabel')" :hint="t('individualReportPathHelp')">
      <EditorPathInput
        v-model="individualReportPath"
        mode="existing-file"
        :scopes="['site']"
        content-type-filter="report"
        placeholder="/reports/individual"
        aria-label="Individual report path"
      />
    </EditorField>

    <EditorField :label="t('selectedConstructIdsLabel')" :hint="t('selectedConstructIdsHelp')">
      <Input v-model="selectedConstructIds" class="h-8 rounded-sm bg-muted text-[13px]" placeholder="101, 205, 309" />
    </EditorField>

    <EditorField :label="t('sortColumnLabel')">
      <Input v-model="sortColumn" class="h-8 rounded-sm bg-muted text-[13px]" placeholder="score" />
    </EditorField>

    <EditorField :label="t('ascendingLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("ascendingHint") }}</span>
        <Switch v-model="sortAscending" />
      </div>
    </EditorField>

    <EditorField :label="t('colorAccessorLabel')">
      <Textarea v-model="colorAccessor" rows="5" class="rounded-sm bg-muted font-mono text-[12px]" />
    </EditorField>

    <EditorField :label="t('iconAccessorLabel')" :hint="t('iconAccessorHelp')">
      <Textarea v-model="iconAccessor" rows="4" class="rounded-sm bg-muted font-mono text-[12px]" />
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Competency Descriptions"
  individualReportPathLabel: "Individual Report Path"
  individualReportPathHelp: "Path to navigate to when a competency is clicked."
  selectedConstructIdsLabel: "Selected Competency IDs"
  selectedConstructIdsHelp: "Comma-separated construct IDs to include. Leave blank for all constructs."
  sortColumnLabel: "Sort Column"
  ascendingLabel: "Ascending"
  ascendingHint: "Sort from lowest to highest."
  colorAccessorLabel: "Color Accessor Code"
  iconAccessorLabel: "Icon Accessor Code"
  iconAccessorHelp: "JavaScript function returning an icon name based on the construct."
</i18n>
